import * as React from "react";
import { graphql } from "gatsby";
import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import { when } from "../../styles/theme.js";
import PageContainer from "../pageContainer.js";
import { SectionTitle } from "../typography.js";
import { useState } from "react";
import ToggleButton from "../toggleButton.js";
import { useLocalization } from "gatsby-theme-i18n";
import { useInView } from "react-intersection-observer";

export const fragment = graphql`
  fragment DirectorsContent on ContentfulPageSectionDirectors {
    title
    directors {
      position
      name
      description {
        childMarkdownRemark {
          html
        }
      }
      profileImage {
        gatsbyImageData(layout: CONSTRAINED, width: 600, height: 600)
        description
      }
    }
    internal {
      type
    }
  }
`;

const Container = styled(PageContainer)`
  margin-bottom: 10rem;
`;

const InnerContainer = styled.div`
  ${when("sm")} {
    display: flex;
    margin-left: -${(props) => props.theme.gutterWidth.sm / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.sm / 2}rem;
  }

  ${when("md")} {
    margin-left: -${(props) => props.theme.gutterWidth.md / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.md / 2}rem;
  }

  ${when("lg")} {
    margin-left: -${(props) => props.theme.gutterWidth.lg / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.lg / 2}rem;
  }

  ${when("xl")} {
    margin-left: -${(props) => props.theme.gutterWidth.xl / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.xl / 2}rem;
  }

  ${when("xxl")} {
    margin-left: -${(props) => props.theme.gutterWidth.xxl / 2}rem;
    margin-right: -${(props) => props.theme.gutterWidth.xxl / 2}rem;
  }
`;

const DirectorsContainer = styled.div`
  ${when("sm")} {
    flex: 1;
    padding-left: ${(props) => props.theme.gutterWidth.sm / 2}rem;
    padding-right: ${(props) => props.theme.gutterWidth.sm / 2}rem;
  }

  ${when("md")} {
    padding-left: ${(props) => props.theme.gutterWidth.md / 2}rem;
    padding-right: ${(props) => props.theme.gutterWidth.md / 2}rem;
  }

  ${when("lg")} {
    padding-left: ${(props) => props.theme.gutterWidth.lg / 2}rem;
    padding-right: ${(props) => props.theme.gutterWidth.lg / 2}rem;
  }

  ${when("xl")} {
    padding-left: ${(props) => props.theme.gutterWidth.xl / 2}rem;
    padding-right: ${(props) => props.theme.gutterWidth.xl / 2}rem;
  }

  ${when("xxl")} {
    padding-left: ${(props) => props.theme.gutterWidth.xxl / 2}rem;
    padding-right: ${(props) => props.theme.gutterWidth.xxl / 2}rem;
  }
`;

const DirectorsInnerContainer = styled.div`
  margin-top: 5rem;
`;

const DirectorContainer = styled.div`
  padding: 2rem;
  border: 1px solid ${(props) => props.theme.colors.red};
  margin-top: 4rem;
  border-radius: ${(props) => props.theme.gutterWidth.xs}rem;
  text-align: center;
  background: ${(props) => props.theme.colors.white};

  &:first-child {
    margin-top: 0;
  }

  ${when("sm")} {
    display: ${(props) => (props.hideWhenSm ? "none" : "block")};
  }
`;

const Name = styled.h3`
  margin: 2rem 0 0;
  font-weight: 700;
  font-size: 2.4rem;
  color: ${(props) => props.theme.colors.darkBlue};
`;

const Position = styled.div`
  color: ${(props) => props.theme.colors.red};
  font-weight: 500;
  font-size: 1.8rem;
  max-width: 30rem;
  margin: 0 auto;
`;

const Description = styled.div`
  height: ${(props) => (props.isActive ? "auto" : "9.6rem")};
  overflow: hidden;
  margin-top: 4rem;
  position: relative;

  &:after {
    content: "";
    height: 4.8rem;
    background: linear-gradient(
      0deg,
      rgba(255, 255, 255, 1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: ${(props) => (props.isActive ? "none" : "block")};
  }
`;

const ProfileImageContainer = styled.div`
  border-radius: 50%;
  transform: translateZ(0);
  width: 14rem;
  height: 14rem;
  overflow: hidden;
  margin-left: auto;
  margin-right: auto;
  margin-top: 2rem;
`;

const SmDirectorsContainer = styled(DirectorsContainer)`
  display: none;

  ${when("sm")} {
    display: block;
    flex: 1;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Director = ({ data, hideWhenSm }) => {
  const [isActive, setIsActive] = useState(false);
  const { locale } = useLocalization();

  return (
    <DirectorContainer hideWhenSm={hideWhenSm}>
      {data.profileImage && (
        <ProfileImageContainer>
          <GatsbyImage
            image={data.profileImage.gatsbyImageData}
            alt={data.profileImage.description}
          />
        </ProfileImageContainer>
      )}
      <Name>{data.name}</Name>
      <Position>{data.position}</Position>
      <Description
        isActive={isActive}
        dangerouslySetInnerHTML={{
          __html: data.description.childMarkdownRemark.html,
        }}
      />
      <ButtonContainer>
        <ToggleButton
          isActive={isActive}
          locale={locale}
          setIsActive={setIsActive}
        />
      </ButtonContainer>
    </DirectorContainer>
  );
};

const Directors = ({ data, setCurrentSection, index }) => {
  const evenDirectors = data.directors.filter((director, index) => {
    return index % 2 === 0;
  });

  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        setCurrentSection(index);
      }
    },
    rootMargin: "-30% 0% -30% 0%",
  });

  return (
    <Container
      as={"section"}
      hasSideBar={true}
      ref={ref}
      id={`section-${index}`}
    >
      <InnerContainer>
        <DirectorsContainer>
          <SectionTitle>{data.title}</SectionTitle>
          <DirectorsInnerContainer>
            {data.directors.map((director, index) => {
              return (
                <Director
                  key={index}
                  data={director}
                  hideWhenSm={index % 2 === 0}
                />
              );
            })}
          </DirectorsInnerContainer>
        </DirectorsContainer>
        <SmDirectorsContainer>
          {evenDirectors.map((director, index) => {
            return <Director key={index} data={director} />;
          })}
        </SmDirectorsContainer>
      </InnerContainer>
    </Container>
  );
};

export const directorsName = "ContentfulPageSectionDirectors";

export default Directors;
